import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { NbDialogRef, NbDialogService, NbToastrService, NbCardModule, NbIconModule, NbButtonModule, NbInputModule } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { RouterState } from 'src/app/features/auth/redux/router-state';
import { SourceItem } from 'src/app/models/drb-models/source-item';
import { ACTIONS, BUNDLE_NAMES, DRBTYPE, PREFIX } from 'src/app/shared/constants/general-constants';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { DrbState, SaveSourceFilesState, SaveSourceLinksState, SaveSourceVideosState } from '../../../redux';
import { NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-edit-source-item',
    templateUrl: './edit-source-item.component.html',
    styleUrls: ['./edit-source-item.component.css', '../../../../../shared/common_syles.css'],
    imports: [NbCardModule, NgIf, NbIconModule, NbButtonModule, FormsModule, NbInputModule]
})
export class EditSourceItemComponent implements OnInit, OnDestroy {
  public file$: Observable<any[]> = inject(Store).select(DrbState.sourceFiles);
  public link$: Observable<any[]> = inject(Store).select(DrbState.sourceLinks);
  public video$: Observable<any[]> = inject(Store).select(DrbState.sourceVideos);
  public id$: Observable<any[]> = inject(Store).select(DrbState.ids);
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe: Subject<void> = new Subject();

  public action;
  public title;
  public sourceItem: SourceItem;
  public sourceItemState: any[];
  public sourceItemCampaign;
  public username = '';
  public ids;
  public patternId = '^[a-z0-9_]+$';
  public patternLocation = '^[^\'"]*$';
  public patternCampaign = '^[a-z0-9_]+$';
  public type;
  public bundle_name;

  constructor(
    private store: Store,
    protected ref: NbDialogRef<EditSourceItemComponent>,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService
  ) {}

  public ngOnInit() {
    this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
    if (this.type === DRBTYPE.FILE) {
      if (this.action === ACTIONS.EDIT) {
        this.title = $localize`Editing File Source`;
      } else if (this.action === ACTIONS.CREATE) {
        this.title = $localize`Creating File Source`;
      }
      this.file$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.sourceItemState = data));
    }
    if (this.type === DRBTYPE.LINK) {
      if (this.action === ACTIONS.EDIT) {
        this.title = $localize`Editing Link Source`;
      } else if (this.action === ACTIONS.CREATE) {
        this.title = $localize`Creating Link Source`;
      }
      this.link$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.sourceItemState = data));
    }
    if (this.type === DRBTYPE.VIDEO) {
      if (this.action === ACTIONS.EDIT) {
        this.title = $localize`Editing Video Source`;
      } else if (this.action === ACTIONS.CREATE) {
        this.title = $localize`Creating Video Source`;
      }
      this.video$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.sourceItemState = data));
    }
    this.id$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.ids = data));
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public save() {
    const localSourceItem = JSON.parse(JSON.stringify(this.sourceItemState));

    if (this.type === DRBTYPE.LINK) {
      const link = new URL(this.sourceItem.location);
      if (!link.searchParams.has('utm_source')) {
        link.searchParams.set('utm_source', 'app');
      }
      if (!link.searchParams.has('utm_medium')) {
        link.searchParams.set('utm_medium', 'app');
      }
      if (!link.searchParams.has('utm_campaign')) {
        link.searchParams.set('utm_campaign', this.sourceItemCampaign);
      }
      this.sourceItem.location = link.toString();
    }

    if (this.action === ACTIONS.CREATE) {
      const sourceItemId = this.prefix() + this.sourceItem.id + '_dmc';
      if (this.ids.some((item) => item === sourceItemId)) {
        this.toastrService.show('', $localize`${sourceItemId} already exists!`, { status: 'danger' });
      } else {
        this.sourceItem.id = sourceItemId;
        this.sourceItem.location = this.sourceItem.location;
        this.sourceItem.created_by = this.username;
        this.sourceItem.created_ts = Math.floor(Date.now() / 1000);
        this.sourceItem.last_updated_by = this.username;
        this.sourceItem.last_updated_ts = Math.floor(Date.now() / 1000);

        localSourceItem.push(this.sourceItem);
        this.dispatch(localSourceItem);
        this.ref.close();
      }
    } else if (this.action === ACTIONS.EDIT) {
      const index = localSourceItem.findIndex((item) => item.id === this.sourceItem.id);
      localSourceItem[index].location = this.sourceItem.location;
      localSourceItem[index].last_updated_by = this.username;
      localSourceItem[index].last_updated_ts = Math.floor(Date.now() / 1000);

      this.dispatch(localSourceItem);
      this.ref.close();
    }
  }

  public titleCaseWord(word: string) {
    if (!word) {
      return word;
    }

    return word[0].toUpperCase() + word.substring(1).toLowerCase();
  }

  public dispatch(localSourceItem) {
    if (this.type === DRBTYPE.FILE) {
      this.store.dispatch(new SaveSourceFilesState(localSourceItem, this.bundle_name));
    }
    if (this.type === DRBTYPE.LINK) {
      this.store.dispatch(new SaveSourceLinksState(localSourceItem, this.bundle_name));
    }
    if (this.type === DRBTYPE.VIDEO) {
      this.store.dispatch(new SaveSourceVideosState(localSourceItem, this.bundle_name));
    }
  }

  public close() {
    this.dialogService
      .open(NgxGenericModalComponent, {
        context: {
          title: $localize`Confirm Close`,
          message: $localize`Are you sure that you want to close the window? Any changes that you have made will be lost.`
        }
      })
      .onClose.subscribe((data) => {
        if (data) {
          this.ref.close();
        }
      });
  }

  public prefix() {
    if (this.bundle_name === BUNDLE_NAMES.MARKETING) {
      return PREFIX.MKT;
    } else if (this.bundle_name === BUNDLE_NAMES.MARKETING_TRANSLATIONS) {
      return PREFIX.MKT_T;
    }
  }
}
